import { get, post, delete as del } from './api';
import { API_CONFIG } from '../config/api.config';

// Define types
export interface UserInvitation {
  id: string;
  email: string;
  companyName: string;
  companyId: string;
  inviterName: string;
  roleId: string;
  status: 'PENDING' | 'USED' | 'EXPIRED' | 'REVOKED';
  expiryDate: string;
  createdAt: string;
}

export interface UserInvitationRequest {
  email: string;
  companyId?: string;
  frontendBaseUrl: string;
  roleId?: string;
}

export interface UserInvitationAcceptRequest {
  token: string;
  name: string;
  password: string;
  phoneNumber?: string;
}

// Service functions
export const invitationService = {
  // Get all invitations for the current user's company
  getInvitations: async (): Promise<UserInvitation[]> => {
    return get<UserInvitation[]>(API_CONFIG.endpoints.invitations.list)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        console.error('Error fetching invitations:', error);
        throw error;
      });
  },

  // Get pending invitations for the current user's company
  getPendingInvitations: async (): Promise<UserInvitation[]> => {
    return get<UserInvitation[]>(API_CONFIG.endpoints.invitations.pending)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        console.error('Error fetching pending invitations:', error);
        throw error;
      });
  },

  // Create a new invitation
  createInvitation: async (
    invitationData: UserInvitationRequest
  ): Promise<UserInvitation> => {
    return post<UserInvitation>(
      API_CONFIG.endpoints.invitations.create,
      invitationData as unknown as Record<string, unknown>
    )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        console.error('Error creating invitation:', error);
        throw error;
      });
  },

  // Validate an invitation token
  validateInvitation: async (token: string): Promise<UserInvitation> => {
    return get<UserInvitation>(API_CONFIG.endpoints.invitations.validate(token))
      .then((response) => {
        return response;
      })
      .catch((error) => {
        console.error('Error validating invitation:', error);
        throw error;
      });
  },

  // Accept an invitation
  acceptInvitation: async (
    acceptData: UserInvitationAcceptRequest
  ): Promise<Record<string, unknown>> => {
    return post(
      API_CONFIG.endpoints.invitations.accept,
      acceptData as unknown as Record<string, unknown>
    )
      .then((response) => {
        return response as Record<string, unknown>;
      })
      .catch((error) => {
        console.error('Error accepting invitation:', error);
        throw error;
      });
  },

  // Revoke an invitation
  revokeInvitation: async (invitationId: string): Promise<void> => {
    return del(API_CONFIG.endpoints.invitations.revoke(invitationId))
      .then(() => {
        return;
      })
      .catch((error) => {
        console.error('Error revoking invitation:', error);
        throw error;
      });
  },

  // Resend an invitation
  resendInvitation: async (
    invitationId: string,
    frontendBaseUrl: string,
    email?: string
  ): Promise<void> => {
    const data: Record<string, unknown> = {
      frontendBaseUrl
    };

    if (email) {
      data.email = email;
    }

    return post(API_CONFIG.endpoints.invitations.resend(invitationId), data)
      .then(() => {
        return;
      })
      .catch((error) => {
        console.error('Error resending invitation:', error);
        throw error;
      });
  }
};
